import Box from '@mui/material/Box'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography/Typography'
import S3Avatar from 'Components/S3Image/S3Avatar'
import PersonIcon from '@mui/icons-material/Person'
import PhoneIcon from '@mui/icons-material/Phone'
import EmailIcon from '@mui/icons-material/Email'
import DateRangeIcon from '@mui/icons-material/DateRange'
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined'
import { AsYouType } from 'libphonenumber-js'
import { BirthdayToAgeInYearsAndMonths } from 'core/Utilities'
import Button from '@mui/material/Button'
import { MenuButton } from 'Components/Menu/MenuButton'
import { sendPost } from 'hooks/use-fetch'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { usePermissions } from 'hooks/use-permissions'

interface Props {
  caregiver: CaregiverDetailsModel | null
  caregiverID: number
  cardCaption?: string
  onEditClick?: () => void
}

export interface CaregiverDetailsModel {
  firstName?: string
  lastName?: string
  relationship?: string
  isFinanciallyResponsible?: boolean
  livesWithChild?: boolean
  primaryLanguage?: string
  phone?: string
  email?: string
  dateOfBirth?: Date | null
  address?: string
  city?: string
  state?: string
  zip?: string
  childFirstName?: string
  s3Key?: string
}

export const CaregiverDetailsDisplay = (props: Props): JSX.Element => {
  const { caregiver, cardCaption, onEditClick } = props
  const perms = usePermissions()
  const [sent, setSent] = useState(false)
  const sendPasswordReset = async (): Promise<void> => {
    if (!sent) {
      setSent(true)
      const resp = await sendPost('/CaregiverDetails/SendPasswordReset', { id: props.caregiverID })
      if (resp.success) {
        toast.success('Password Reset Link Sent')
      } else {
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        toast.error('Unable to send password reset: ' + resp.error)
        setSent(false)
      }
    }
  }
  const doPasswordReset = (): void => {
    void sendPasswordReset()
  }
  if (caregiver == null) { return <Skeleton /> }
  return <Box className='CardDisplay' data-testid='caregiverDetailsCard'>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'row' }}>
      <Typography variant='h6' sx={{ fontWeight: 'bold' }} gutterBottom>{cardCaption ?? 'Caregiver Details'}</Typography>
      {
      perms.CareNetwork_Caregiver_Edit && (
        <MenuButton options={[{ id: 1, name: 'Send Password Reset', onClick: doPasswordReset, warning: '' }]} onContinue={() => {}} activeSelection={undefined} setActiveSelection={() => {}}></MenuButton>
      )
    }
    </Box>
    <Stack direction='column' spacing={1}>
      <Stack direction='row' spacing={1}>
        {caregiver?.s3Key !== null && caregiver?.s3Key != null ? <S3Avatar imageKey={caregiver?.s3Key} height='30px' width='30px' /> : <PersonIcon />}
        <Stack direction='column'>
          <Typography variant='body1' sx={{ fontWeight: 'bold', overflowWrap: 'break-word' }}>
            {`${caregiver?.firstName ?? ''} ${caregiver?.lastName ?? ''}`}
          </Typography>
          {caregiver?.relationship != null && <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>
            {caregiver?.relationship}
          </Typography>}
          {caregiver?.isFinanciallyResponsible != null && <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>
            {caregiver?.isFinanciallyResponsible ? `Financially Responsible for ${caregiver?.childFirstName ?? ''}` : `Not Financially Responsible for ${caregiver?.childFirstName ?? ''}`}
          </Typography>}
          {caregiver?.livesWithChild != null && <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>
            {caregiver?.livesWithChild ? `Lives With ${caregiver?.childFirstName ?? ''}` : `Does Not Live With ${caregiver?.childFirstName ?? ''}`}
          </Typography>}
          <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>
            {`Speaks ${caregiver?.primaryLanguage ?? 'English'}`}
          </Typography>
        </Stack>
      </Stack>
      {(caregiver?.phone ?? '') !== '' && <Stack direction='row' spacing={1}>
        <PhoneIcon />
        <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>
          {new AsYouType('US').input(caregiver.phone ?? '')}
        </Typography>
      </Stack>}
      {(caregiver?.email ?? '') !== '' && <Stack direction='row' spacing={1}>
        <EmailIcon />
        <Typography variant='body1' sx={{ overflowWrap: 'anywhere' }}>
          {<a href={`mailto:${caregiver?.email ?? ''}`}>
            {caregiver?.email}
          </a>}
        </Typography>
      </Stack>}
      {caregiver?.dateOfBirth != null && caregiver?.dateOfBirth !== null && <Stack direction='row' spacing={1}>
        <DateRangeIcon />
        <Stack direction='row' spacing={1} flexWrap='wrap' alignItems='center'>
          <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>
            {(caregiver?.dateOfBirth !== null && caregiver?.dateOfBirth != null && new Date(caregiver?.dateOfBirth).toLocaleString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            }))}
          </Typography>
          {caregiver !== null && <Typography variant='body2' sx={{ overflowWrap: 'break-word' }}>
            ({BirthdayToAgeInYearsAndMonths(caregiver.dateOfBirth, false)})
          </Typography>}
        </Stack>
      </Stack>}
      {caregiver?.address != null && <Stack direction='row' spacing={1}>
        <PlaceOutlinedIcon />
        <Stack direction='column'>
          <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>
            {caregiver?.address}
          </Typography>
          <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>
            {`${caregiver?.city ?? ''}, ${caregiver?.state ?? ''}`}
          </Typography>
          <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>
            {caregiver?.zip}
          </Typography>
        </Stack>
      </Stack>}
      {onEditClick != null && <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          variant='contained'
          onClick={onEditClick}
          sx={{ textTransform: 'none', px: 4 }}
        >
          Edit Caregiver
        </Button>
      </Box>}
    </Stack>
  </Box>
}
